/**
 * CycleVersionFlags — on a cycle and its priority list: the items groups in this
 * cycle ranked on different versions (migration 928). Each links to the item's
 * History, where the flag is settled. Never blocks anything (owner ruling R3);
 * it shows while any flag is unsettled, and says nothing when there is none.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Callout } from '@/components/ds';
import { fetchCycleVersionFlags, type CycleVersionFlag } from '@/lib/itemVersionsService';
import { itemInContextHref } from '@/lib/itemCycleStanding';

export function CycleVersionFlags({ cycleId, className = '' }: { cycleId: string; className?: string }) {
  const [flags, setFlags] = useState<CycleVersionFlag[] | null>(null);

  useEffect(() => {
    let live = true;
    void fetchCycleVersionFlags(cycleId).then((f) => {
      if (live) setFlags(f);
    });
    return () => {
      live = false;
    };
  }, [cycleId]);

  const open = (flags ?? []).filter((f) => !f.settled);
  if (open.length === 0) return null;
  const settledCount = (flags ?? []).length - open.length;

  return (
    <div className={className}>
      <Callout
        tone="mustard"
        label="Different versions ranked"
        title={`${open.length} ${open.length === 1 ? 'item was' : 'items were'} ranked on different versions in this cycle`}
      >
        <p>Each group judged what the item said at the time. Decide on each item's History whether the differences matter. This does not block ratifying.</p>
        <ul className="mt-2 space-y-1">
          {open.map((f) => (
            <li key={f.itemId}>
              <Link to={itemInContextHref(f.itemId, { cycleId, tab: 'history' })} className="underline underline-offset-2 hover:text-[var(--text-pri)]">
                {f.itemTitle}
              </Link>
              <span className="text-[var(--text-dim)]"> · versions {f.versions.join(', ')}</span>
            </li>
          ))}
        </ul>
        {settledCount > 0 && <p className="mt-2 text-[13px]">{settledCount} more already settled.</p>}
      </Callout>
    </div>
  );
}

export default CycleVersionFlags;
